import React, { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Grid, Box, Button, Typography, IconButton, Drawer, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, Visibility as VisibilityIcon, Delete as DeleteIcon } from '@mui/icons-material';
import BaseWidget, { WidgetSize } from './BaseWidget';

import ActionsRequiredWidget from './widgets/ActionsRequiredWidget';
import NextTimeOffWidget from './widgets/NextTimeOffWidget';
import KPIWidget from './widgets/KPIWidget';
import MyTimesheetWidget from './widgets/MyTimesheetWidget';
import TeamAvailabilityWidget from './widgets/TeamAvailabilityWidget';
import GalleryWidget from './widgets/GalleryWidget';
import RecentProjectsWidget from './widgets/RecentProjectsWidget';
import MyOpportunitiesWidget from './widgets/MyOpportunitiesWidget';

export interface WidgetConfig {
    id: string;
    type: 'documents' | 'actions' | 'timeoff' | 'kpi' | 'timesheet' | 'availability' | 'gallery' | 'projects' | 'my_opportunities';
    size: WidgetSize;
    title: string;
}

const AVAILABLE_WIDGETS: Omit<WidgetConfig, 'id' | 'size'>[] = [

    { type: 'actions', title: 'Actions Required' },
    { type: 'timeoff', title: 'Next Time Off' },
    { type: 'kpi', title: 'Startups & KPIs' },
    { type: 'timesheet', title: 'My Timesheet' },
    { type: 'availability', title: 'Team Availability' },
    { type: 'gallery', title: 'Gallery' },
    { type: 'projects', title: 'Recent Projects' },
    { type: 'my_opportunities', title: 'My Leads' },
];

const DEFAULT_LAYOUT: WidgetConfig[] = [
    { id: '1', type: 'actions', size: 'medium', title: 'Actions Required' },
    { id: '2', type: 'timeoff', size: 'small', title: 'Next Time Off' },
    { id: '4', type: 'kpi', size: 'small', title: 'Startups & KPIs' },
    { id: '5', type: 'timesheet', size: 'medium', title: 'My Timesheet' },
    { id: '6', type: 'availability', size: 'medium', title: 'Team Availability' },
    { id: '7', type: 'gallery', size: 'medium', title: 'Gallery' },
];

// Helper to get grid xs/md props based on size
const getGridProps = (size: WidgetSize) => {
    switch (size) {
        case 'large': return { xs: 12, sm: 12 };
        case 'medium': return { xs: 12, sm: 8 };
        case 'small': return { xs: 12, sm: 4 };
        default: return { xs: 12, sm: 4 };
    }
};

const renderWidgetContent = (widget: WidgetConfig) => {
    switch (widget.type) {
        case 'actions':
            return <ActionsRequiredWidget />;
        case 'timeoff':
            return <NextTimeOffWidget />;
        case 'kpi':
            return <KPIWidget />;
        case 'timesheet':
            return <MyTimesheetWidget />;
        case 'availability':
            return <TeamAvailabilityWidget />;
        case 'gallery':
            return <GalleryWidget />;
        case 'projects':
            return <RecentProjectsWidget />;
        case 'my_opportunities':
            return <MyOpportunitiesWidget />;
        default:
            return <Typography>Unknown Widget Type</Typography>;
    }
};

// Sortable widget wrapper component
interface SortableWidgetProps {
    widget: WidgetConfig;
    isEditMode: boolean;
    onRemove: () => void;
    onResize: (newSize: WidgetSize) => void;
}

const SortableWidget: React.FC<SortableWidgetProps> = ({ widget, isEditMode, onRemove, onResize }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: widget.id, disabled: !isEditMode });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <Grid
            item
            {...getGridProps(widget.size)}
            ref={setNodeRef}
            style={style}
        >
            <BaseWidget
                title={widget.type === 'gallery' ? '' : widget.title}
                size={widget.size}
                isEditMode={isEditMode}
                onRemove={onRemove}
                onResize={onResize}
                dragHandleProps={isEditMode ? { ...attributes, ...listeners } : undefined}
            >
                {renderWidgetContent(widget)}
            </BaseWidget>
        </Grid>
    );
};

const DashboardGrid: React.FC = () => {
    const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px of movement before activating drag
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        const savedLayout = localStorage.getItem('dashboard_layout');
        if (savedLayout) {
            try {
                setWidgets(JSON.parse(savedLayout));
            } catch (e) {
                console.error('Failed to parse dashboard layout', e);
                setWidgets(DEFAULT_LAYOUT);
            }
        } else {
            setWidgets(DEFAULT_LAYOUT);
        }
    }, []);

    const saveLayout = (newLayout: WidgetConfig[]) => {
        setWidgets(newLayout);
        localStorage.setItem('dashboard_layout', JSON.stringify(newLayout));
    };

    const handleResize = (id: string, newSize: WidgetSize) => {
        const newLayout = widgets.map(w => w.id === id ? { ...w, size: newSize } : w);
        saveLayout(newLayout);
    };

    const handleRemove = (id: string) => {
        const newLayout = widgets.filter(w => w.id !== id);
        saveLayout(newLayout);
    };

    const handleAddWidget = (type: string, title: string) => {
        const newWidget: WidgetConfig = {
            id: Date.now().toString(),
            type: type as any,
            size: 'medium',
            title
        };
        saveLayout([...widgets, newWidget]);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = widgets.findIndex(w => w.id === active.id);
            const newIndex = widgets.findIndex(w => w.id === over.id);
            const newWidgets = arrayMove(widgets, oldIndex, newIndex);
            saveLayout(newWidgets);
        }
    };

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" color="text.secondary">
                    {isEditMode ? 'Customizing Dashboard' : ''}
                </Typography>
                <Box>
                    {!isEditMode ? (
                        <Button
                            startIcon={<EditIcon />}
                            onClick={() => setIsEditMode(true)}
                            variant="outlined"
                            size="small"
                        >
                            Customize
                        </Button>
                    ) : (
                        <>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={() => setIsDrawerOpen(true)}
                                variant="contained"
                                color="secondary"
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                Add Widget
                            </Button>
                            <Button
                                startIcon={<SaveIcon />}
                                onClick={() => setIsEditMode(false)}
                                variant="contained"
                                size="small"
                            >
                                Done
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={widgets.map(w => w.id)}
                    strategy={rectSortingStrategy}
                >
                    <Grid container spacing={3}>
                        {widgets.map((widget) => (
                            <SortableWidget
                                key={widget.id}
                                widget={widget}
                                isEditMode={isEditMode}
                                onRemove={() => handleRemove(widget.id)}
                                onResize={(newSize) => handleResize(widget.id, newSize)}
                            />
                        ))}
                    </Grid>
                </SortableContext>
            </DndContext>

            {/* Add Widget Drawer */}
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
            >
                <Box sx={{ width: 300, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Add Widget</Typography>
                        <IconButton onClick={() => setIsDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <List>
                        {AVAILABLE_WIDGETS.map((w, index) => {
                            const isAdded = widgets.some(widget => widget.type === w.type);
                            return (
                                <ListItem
                                    button
                                    key={index}
                                    onClick={() => {
                                        if (isAdded) {
                                            const newLayout = widgets.filter(widget => widget.type !== w.type);
                                            saveLayout(newLayout);
                                        } else {
                                            handleAddWidget(w.type, w.title);
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        {isAdded ? <DeleteIcon color="error" /> : <VisibilityIcon color="primary" />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={w.title}
                                        secondary={isAdded ? 'Click to remove' : 'Click to add'}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </Drawer>

        </Box>
    );
};

export default DashboardGrid;
